import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ClassSelect } from "@/components/StudentPicker";
import { scoreOf } from "@/components/ProgressChart";
import { exportPdf } from "@/lib/feeExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trophy, FileDown } from "lucide-react";

const MEDAL = ["bg-amber-400", "bg-slate-300", "bg-orange-300"];
const avg = (a) => (a.length ? Math.round((a.reduce((x, y) => x + y, 0) / a.length) * 10) / 10 : null);

function compute(reports) {
  const subj = {};
  const students = reports.map((r) => {
    const vals = [];
    r.subjects.forEach((s) => { const v = scoreOf(s); if (v != null) { vals.push(v); (subj[s.subject] = subj[s.subject] || []).push({ v, name: r.student_name }); } });
    return { id: r.student_id, name: r.student_name, subjects: vals.length, avg: avg(vals) };
  }).filter((s) => s.avg != null).sort((a, b) => b.avg - a.avg);
  let rank = 0;
  students.forEach((s, i) => { rank = i > 0 && s.avg === students[i - 1].avg ? rank : i + 1; s.rank = rank; });
  const subjects = Object.entries(subj).map(([name, list]) => {
    const top = list.reduce((a, b) => (b.v > a.v ? b : a));
    return { name, count: list.length, avg: avg(list.map((x) => x.v)), high: top.v, topper: list.filter((x) => x.v === top.v).map((x) => x.name).join(", "), low: Math.min(...list.map((x) => x.v)) };
  }).sort((a, b) => a.name.localeCompare(b.name));
  return { students, subjects, classAvg: avg(students.map((s) => s.avg)) };
}

export function ClassResults({ yearNow }) {
  const [cls, setCls] = useState({ branch_id: "", class_name: "", section: "" });
  const [meta, setMeta] = useState({ academic_year: yearNow, term: "Term 1" });
  const [reports, setReports] = useState([]);
  useEffect(() => {
    if (!cls.class_name || !cls.section || !meta.term || !/^\d{4}-\d{2}$/.test(meta.academic_year)) { setReports([]); return; }
    const t = setTimeout(() => {
      api.get("/progress-reports", { params: { class_name: cls.class_name, section: cls.section, branch_id: cls.branch_id || undefined, term: meta.term, academic_year: meta.academic_year, limit: 100 } })
        .then((r) => setReports(r.data.items)).catch((e) => toast.error(formatApiError(e)));
    }, 300);
    return () => clearTimeout(t);
  }, [cls.branch_id, cls.class_name, cls.section, meta.term, meta.academic_year]);
  const { students, subjects, classAvg } = useMemo(() => compute(reports), [reports]);
  const title = `${cls.class_name}-${cls.section} • ${meta.term} ${meta.academic_year}`;
  const pdf = () => exportPdf(`Class results ${title}`, [{ key: "rank", label: "Rank" }, { key: "name", label: "Student" }, { key: "subjects", label: "Subjects" }, { key: "avg", label: "Average %" }], students,
    `Class average ${classAvg ?? "—"}% • Subject averages: ${subjects.map((s) => `${s.name} ${s.avg}%`).join(", ")}`);

  return (
    <div data-testid="class-results" className="space-y-5 mb-6">
      <div className="bg-white rounded-2xl shadow-sm p-5 flex flex-wrap gap-3 items-end">
        <ClassSelect value={cls} onChange={setCls} />
        <div><Label>Academic year</Label><Input data-testid="results-year" value={meta.academic_year} onChange={(e) => setMeta({ ...meta, academic_year: e.target.value })} className="mt-1 w-28" /></div>
        <div><Label>Term</Label><Input data-testid="results-term" list="pr-terms" value={meta.term} onChange={(e) => setMeta({ ...meta, term: e.target.value })} className="mt-1 w-36" /></div>
        {students.length > 0 && <Button variant="outline" data-testid="results-pdf" onClick={pdf} className="ml-auto"><FileDown size={15} className="mr-1" /> PDF</Button>}
      </div>
      {!cls.class_name ? <p className="text-sm text-slate-500">Choose a class to see results.</p> : students.length === 0 ? <p data-testid="results-empty" className="text-sm text-slate-500 bg-white rounded-2xl p-5">No marks posted for {title} yet.</p> : (<>
        <div className="grid sm:grid-cols-4 gap-3">
          {students.filter((s) => s.rank <= 3).slice(0, 3).map((s) => (
            <div key={s.id} data-testid={`topper-${s.rank}-${s.id}`} className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3">
              <span className={`h-10 w-10 rounded-full ${MEDAL[s.rank - 1]} flex items-center justify-center text-white font-extrabold`}>{s.rank}</span>
              <div className="min-w-0"><div className="font-bold truncate">{s.name}</div><div className="text-xs text-slate-500">{s.avg}% average</div></div>
            </div>
          ))}
          <div data-testid="results-class-avg" className="bg-violet-600 text-white rounded-2xl shadow-sm p-4 sm:col-start-4">
            <Trophy size={18} /><div className="font-display text-2xl font-extrabold mt-1">{classAvg}%</div><div className="text-xs text-white/80">Class average • {students.length} students</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
          <div className="px-4 pt-3 font-display font-bold">Subject-wise class average</div>
          <table className="w-full text-sm mt-2">
            <thead className="bg-violet-50"><tr><th className="text-left px-4 py-2">Subject</th><th className="px-3">Students</th><th className="px-3">Average</th><th className="px-3">Highest</th><th className="text-left px-3">Subject topper</th><th className="px-3">Lowest</th></tr></thead>
            <tbody className="divide-y divide-slate-100">{subjects.map((s) => (
              <tr key={s.name} data-testid={`subject-avg-${s.name}`}>
                <td className="px-4 py-2 font-semibold">{s.name}</td><td className="text-center">{s.count}</td>
                <td className="text-center"><div className="flex items-center gap-2 justify-center"><div className="w-20 h-2 bg-violet-100 rounded-full overflow-hidden"><div className="h-full bg-violet-600" style={{ width: `${s.avg}%` }} /></div><b>{s.avg}%</b></div></td>
                <td className="text-center text-emerald-700 font-semibold">{s.high}%</td><td className="px-3">{s.topper}</td><td className="text-center text-rose-700">{s.low}%</td>
              </tr>))}</tbody>
          </table>
        </div>
        <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
          <div className="px-4 pt-3 font-display font-bold">Class rank</div>
          <table className="w-full text-sm mt-2">
            <thead className="bg-violet-50"><tr><th className="px-4 py-2 w-16">Rank</th><th className="text-left px-3">Student</th><th className="px-3">Subjects</th><th className="px-3">Average</th></tr></thead>
            <tbody className="divide-y divide-slate-100">{students.map((s) => (
              <tr key={s.id} data-testid={`rank-row-${s.id}`}><td className="text-center font-bold text-violet-700 py-2">{s.rank}</td><td className="px-3 font-semibold">{s.name}</td><td className="text-center">{s.subjects}</td><td className="text-center font-bold">{s.avg}%</td></tr>
            ))}</tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400">Scores use marks (e.g. 45/50 or 90%); where only a grade is given it's converted (A+ ≈ 95, A ≈ 85, B ≈ 65 …). Rank uses each student's average across their subjects.</p>
      </>)}
    </div>
  );
}
